import { OAuthError, useAuth0 } from "@auth0/auth0-react";
import { UseMutationOptions, UseQueryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getCorrelationHeaders, trackApiCall, trackApiResponse } from "../../app-insights";

import { VITE_API_SITES_BASE_URL } from "@/constants/environment.const";
import { routes } from "@/routes/constants";
import { showNotification } from "@/components/notification";

type ApiOptions = RequestInit & { audience: string; scope: string };
type GetTokenFn = (opts: { authorizationParams: { audience: string; scope: string } }) => Promise<string | undefined>;

/** Thrown by processResponse for non-2xx HTTP responses (already tracked & optionally notified). */
export class ApiResponseError extends Error {
  constructor(
    public readonly body: unknown,
    public readonly statusCode: number,
  ) {
    super(typeof body === "object" && body !== null && "message" in body ? String(body.message) : JSON.stringify(body));
    this.name = "ApiResponseError";
  }
}

async function acquireAccessToken(
  audience: string,
  scope: string,
  getTokenSilently: GetTokenFn,
  getTokenWithPopup: GetTokenFn,
  trackingLabel: string,
  startTime: number,
): Promise<string> {
  try {
    const token = await getTokenSilently({ authorizationParams: { audience, scope } });
    if (!token) throw new Error("Access token retrieval failed");
    return token;
  } catch (error) {
    if ((error as OAuthError).error === "consent_required") {
      try {
        const token = await getTokenWithPopup({ authorizationParams: { audience, scope } });
        if (!token) throw new Error("Access token retrieval failed");
        return token;
      } catch {
        // Fall through to tracking/notification below
      }
    }
    const duration = performance.now() - startTime;
    trackApiResponse(trackingLabel, false, duration, { error: "Access token retrieval failed" });
    showNotification({ variant: "error", message: "Access token retrieval failed" });
    throw new Error("Access token retrieval failed");
  }
}

/** Callers expecting HTTP 204 must use T = void so undefined is type-safe. */
async function processResponse<T>(
  res: Response,
  trackingLabel: string,
  method: string,
  url: string,
  duration: number,
  options?: { showErrorNotification?: boolean },
): Promise<T> {
  const success = res.status >= 200 && res.status < 300;

  if (!success) {
    const rawText = await res.text();
    let error: unknown;
    try {
      error = JSON.parse(rawText);
    } catch {
      error = rawText || res.statusText;
    }
    trackApiResponse(trackingLabel, false, duration, {
      statusCode: res.status,
      method,
      url,
      error: typeof error === "string" ? error : JSON.stringify(error),
    });
    if (options?.showErrorNotification) {
      const message = typeof error === "string" ? error : JSON.stringify(error);
      showNotification({ variant: "error", message });
    }
    throw new ApiResponseError(error, res.status);
  }

  // Handle 204 No Content (no body to parse)
  if (res.status === 204) {
    trackApiResponse(trackingLabel, true, duration, {
      statusCode: res.status,
      method,
      url,
      hasData: false,
    });
    return undefined as T;
  }

  const contentType = res.headers.get("content-type");
  const rawText = await res.text();

  let data: T;
  if (!rawText) {
    data = undefined as T;
  } else if (contentType?.includes("text/csv")) {
    data = rawText as T;
  } else {
    try {
      data = JSON.parse(rawText);
    } catch {
      data = rawText as T;
    }
  }

  trackApiResponse(trackingLabel, true, duration, {
    statusCode: res.status,
    method,
    url,
    contentType,
    hasData: !!data,
  });

  return data;
}

export const useApi = <TResult>(
  url: string,
  options: ApiOptions,
  cacheKey: unknown[] = [url],
  enabled: boolean = true,
  queryOption?: Partial<UseQueryOptions<TResult, Error>>,
): { error?: Error; isLoading: boolean; data?: TResult; refetch: () => void } => {
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();

  const fetchData = async (): Promise<TResult> => {
    const { audience, scope, ...fetchOptions } = options;
    const method = fetchOptions.method || "GET";
    const startTime = performance.now();
    const trackingLabel = `REST:${method} ${url}`;

    trackApiCall(trackingLabel, { method, url, audience, scope });

    const accessToken = await acquireAccessToken(
      audience,
      scope,
      getAccessTokenSilently,
      getAccessTokenWithPopup,
      trackingLabel,
      startTime,
    );

    try {
      const res = await fetch(url, {
        ...fetchOptions,
        headers: {
          ...fetchOptions.headers,
          Authorization: `Bearer ${accessToken}`,
          ...getCorrelationHeaders(),
        },
      });

      const duration = performance.now() - startTime;
      return await processResponse<TResult>(res, trackingLabel, method, url, duration, {
        showErrorNotification: true,
      });
    } catch (error) {
      if (error instanceof ApiResponseError) throw error;
      const duration = performance.now() - startTime;
      const message = error instanceof Error ? error.message : "Unknown fetch error";
      trackApiResponse(trackingLabel, false, duration, { method, url, error: message });
      showNotification({ variant: "error", message });
      throw error;
    }
  };

  const { isLoading, data, error, refetch } = useQuery<TResult, Error>({
    queryKey: cacheKey,
    enabled,
    queryFn: fetchData,
    ...queryOption,
  });

  return { error: error || undefined, isLoading, data, refetch };
};

/**
 * `url` may be a function of the mutation variables, for endpoints whose path
 * depends on a per-call id; pass an explicit `cacheKey` in that case. `toBody`
 * maps the variables to the JSON body (return undefined for body-less calls);
 * without it the variables are sent as-is.
 */
export const useApiAsync = <TResult, TVars = void>(
  url: string | ((vars: TVars) => string),
  options: ApiOptions & { toBody?: (vars: TVars) => unknown },
  cacheKey: unknown[] = typeof url === "string" ? [url] : [],
  queryOption?: UseMutationOptions<TResult, Error, TVars>,
) => {
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();

  const fetchData = async (vars: TVars): Promise<TResult> => {
    const { audience, scope, toBody, ...fetchOptions } = options;
    const resolvedUrl = typeof url === "function" ? url(vars) : url;
    const method = fetchOptions.method || "POST";
    const startTime = performance.now();
    const trackingLabel = `REST:${method} ${resolvedUrl}`;

    trackApiCall(trackingLabel, { method, url: resolvedUrl, audience, scope });

    const accessToken = await acquireAccessToken(
      audience,
      scope,
      getAccessTokenSilently,
      getAccessTokenWithPopup,
      trackingLabel,
      startTime,
    );

    const body = toBody ? toBody(vars) : vars;

    try {
      const res = await fetch(resolvedUrl, {
        ...fetchOptions,
        headers: {
          ...fetchOptions.headers,
          Authorization: `Bearer ${accessToken}`,
          ...getCorrelationHeaders(),
        },
        body: body != null ? JSON.stringify(body) : undefined,
      });

      const duration = performance.now() - startTime;
      return await processResponse<TResult>(res, trackingLabel, method, resolvedUrl, duration);
    } catch (error) {
      if (error instanceof ApiResponseError) throw error;
      const duration = performance.now() - startTime;
      trackApiResponse(trackingLabel, false, duration, {
        method,
        url: resolvedUrl,
        error: error instanceof Error ? error.message : "Unknown fetch error",
      });
      throw error;
    }
  };

  const queryClient = useQueryClient();

  const { mutateAsync, isPending, error, data } = useMutation({
    mutationFn: fetchData,
    onSuccess: async () => {
      if (cacheKey.length > 0) {
        queryClient.invalidateQueries({ queryKey: cacheKey });
      }
    },
    ...queryOption,
  });

  return { mutateAsync, isPending, error, data };
};

export const useAuthenticatedApiAsync = () => {
  const { getAccessTokenSilently, getAccessTokenWithPopup, user } = useAuth0();
  const fetchData = async (fetchOptions: RequestInit & { url?: string }) => {
    let accessToken;
    const url = fetchOptions.url ?? VITE_API_SITES_BASE_URL;
    if (user) {
      const fetchAccessToken = async () => {
        try {
          accessToken = await getAccessTokenSilently();
        } catch (e) {
          if ((e as OAuthError).error === "consent_required") {
            accessToken = await getAccessTokenWithPopup();
          } else {
            showNotification({ variant: "error", message: (e as Error).message });
            window.location.replace(routes.error.path);
          }
        }
      };
      await fetchAccessToken();
    }

    if (accessToken) {
      const res = await fetch(url, {
        ...fetchOptions,
        headers: {
          ...fetchOptions.headers,
          "Apollo-Require-Preflight": "true",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (res.status !== 200 && res.status !== 201) {
        const rawText = await res.text();
        let error: { message?: string };
        try {
          error = JSON.parse(rawText);
        } catch {
          error = { message: rawText || res.statusText };
        }
        showNotification({ variant: "error", message: `[GraphQL error]: ${error.message}` });
        throw new Error(error.message);
      } else {
        try {
          const data = await res.json();
          if (data.errors) {
            showNotification({ variant: "error", message: `[GraphQL error]: ${data.errors[0].message}` });
            throw new Error(data.errors[0].message);
          }
          return data;
        } catch {
          /* empty */
        }
      }
    } else {
      throw new Error("Access token not found");
    }
  };

  const { isPending, data, error, mutateAsync } = useMutation({ mutationFn: fetchData });

  return { error: error, loading: isPending, data: data, mutateAsync };
};
